import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { RATIO, TypographySize, SEMANTIC_TAG } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../../theming/useTheme';
import {
  CardComponentDataProps,
  CardComponentPropsFromLayout,
  listAuthors,
  showAuthorInAnyScreenSize,
  showDateInAnyScreenSize,
  showMetadataInAnyScreenSize,
  showMetadataInLargeScreenSize,
  showMetadataInMediumScreenSize,
  showMetadataInSmallScreenSize,
  showPipeInAnyScreenSize,
} from '../cards.utils';
import { WrapperForCard } from '../WrapperForCard';
import { Link } from '../../link/Link';
import { ConditionalRelativeWrap } from '../../ConditionalRelativeWrap';
import { Image } from '../../image/Image';
import { DecorationLine } from '../../DecorationLine';
import { semanticCSS } from '../../../../typography/semanticTags';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import { DynamicSemanticTag } from '../../../../typography/semanticTags/DynamicSemanticTag';
import { createImageAspectRatios } from '../../image/image.utils';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { baseUnit } from '../../../../theming/baseUnitDefinition';
import { ellipsify } from '../../../../typography/ellipsify';
import { TranslatedFontSize } from '../../../../theming/fontSizeTranslator';
import { getLineHeightBySize } from '../../../../typography/semanticTags/semanticTags.utils';
import { PlayIcon } from '../../PlayIcon';

export const componentConfigThemeKey = 'verticalCard';

export type VerticalCardThemeProps = {
  cardBackgroundColor: string;
  cardTitleColorTypeNormal: string;
  cardTitleColorTypeHover: string;
  cardTitleColorTypeActive: string;
  borderRadius: number;
  boxShadowTypeNormal: string;
  boxShadowTypeActive: string;
  boxShadowTypeHover: string;
  borderWidthTypeNormal: number;
  borderWidthTypeHover: number;
  borderWidthTypeActive: number;
  borderColorTypeNormal: string;
  borderColorTypeHover: string;
  borderColorTypeActive: string;
  aspectRatioLarge: RATIO;
  aspectRatioMedium: RATIO;
  aspectRatioSmall: RATIO;
  titleFontSizeLarge: TypographySize;
  titleFontSizeMedium: TypographySize;
  titleFontSizeSmall: TypographySize;
  pipeColor: string;
  showAuthorInLargeScreen: boolean;
  showAuthorInMediumScreen: boolean;
  showAuthorInSmallScreen: boolean;
  showDateInSmallScreen: boolean;
  showDateInMediumScreen: boolean;
  showDateInLargeScreen: boolean;
  metadataFontSizeLarge: TypographySize;
  metadataFontSizeMedium: TypographySize;
  metadataFontSizeSmall: TypographySize;
  authorColor: string;
  dateColor: string;
  wrapperGapLarge: number;
  wrapperGapMedium: number;
  wrapperGapSmall: number;
  verticalGapLarge: number;
  verticalGapMedium: number;
  verticalGapSmall: number;
  horizontalGapLarge: number;
  horizontalGapMedium: number;
  horizontalGapSmall: number;
  titleSemanticTag: SEMANTIC_TAG;
  metadataSemanticTag: SEMANTIC_TAG;
  showMainCategoryInLargeScreen: boolean;
  showMainCategoryInMediumScreen: boolean;
  showMainCategoryInSmallScreen: boolean;
  mainCategorySemanticTag: SEMANTIC_TAG;
  mainCategoryFontSizeLarge: TypographySize;
  mainCategoryFontSizeMedium: TypographySize;
  mainCategoryFontSizeSmall: TypographySize;
  mainCategoryTextColor: string;
  mainCategoryBackgroundColor: string;
  mainCategoryVerticalGapLarge: number;
  mainCategoryVerticalGapMedium: number;
  mainCategoryVerticalGapSmall: number;
  mainCategoryHorizontalGapLarge: number;
  mainCategoryHorizontalGapMedium: number;
  mainCategoryHorizontalGapSmall: number;
  fontSizesStyles: TranslatedFontSize;
  numberOfLinesForTitle: number;
  numberOfLinesForAuthors: number;
};

type VerticalCardProps = CardComponentDataProps & CardComponentPropsFromLayout

const createStyle = (
  cardBackgroundColor: string,
  cardTextColorTypeNormal: string,
  cardTextColorTypeHover: string,
  cardTextColorTypeActive: string,
  borderRadius: number,
  fontSizeMedium: number,
  fontSizeLarge: number,
  numberOfLinesForTitle: number,
) => {
  return StyleSheet.create({
    text: {
      overflow: 'hidden',
      [MEDIA_BREAKPOINTS.large]: {
        height: fontSizeLarge * numberOfLinesForTitle,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: fontSizeMedium * numberOfLinesForTitle,
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: 'fit-content',
      },
    },
    wrapper: {
      width: '100%',
      height: '100%',
      textDecoration: 'none',
      color: cardTextColorTypeNormal,
      backgroundColor: cardBackgroundColor,
      '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
      borderRadius: `${borderRadius}px`,
      position: 'relative',
      ':hover': {
        color: cardTextColorTypeHover,
      },
      ':active': {
        color: cardTextColorTypeActive,
      },
      display: 'inline-block',
    },
  });
};

const createMetadataStyle = (
  horizontalGapLarge: number,
  horizontalGapMedium: number,
  horizontalGapSmall: number,
  showMetadataInLargeScreen: boolean,
  showMetadataInMediumScreen: boolean,
  showMetadataInSmallScreen: boolean,
) => StyleSheet.create({
  style: {
    gridTemplateColumns: 'auto auto max-content',
    width: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMetadataInLargeScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapLarge}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMetadataInMediumScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapMedium}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMetadataInSmallScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapSmall}px`,
    },
  },
}).style;

const createAuthorsStyle = (
  showAuthorSmallScreen: boolean,
  showAuthorMediumScreen: boolean,
  showAuthorLargeScreen: boolean,
  authorColor: string,
  numberOfLinesForAuthors: number,
) => StyleSheet.create({
  style: {
    color: authorColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...showAuthorLargeScreen ? ellipsify(numberOfLinesForAuthors).style : { display: 'none' },
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...showAuthorMediumScreen ? ellipsify(numberOfLinesForAuthors).style : { display: 'none' },
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...showAuthorSmallScreen ? ellipsify(numberOfLinesForAuthors).style : { display: 'none' },
    },
  },
}).style;

const createPipeStyle = (
  showDateSmallScreen: boolean,
  showDateMediumScreen: boolean,
  showDateLargeScreen: boolean,
  showAuthorSmallScreen: boolean,
  showAuthorMediumScreen: boolean,
  showAuthorLargeScreen: boolean,
  pipeColor: string,
) => StyleSheet.create({
  style: {
    color: pipeColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen && showAuthorLargeScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen && showAuthorMediumScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen && showAuthorSmallScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`,
    },
  },
}).style;

const createDateStyle = (
  showDateSmallScreen: boolean,
  showDateMediumScreen: boolean,
  showDateLargeScreen: boolean,
  dateColor: string,
) => StyleSheet.create({
  style: {
    color: dateColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen ? 'inline-block' : 'none',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen ? 'inline-block' : 'none',
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen ? 'inline-block' : 'none',
    },
  },
}).style;

const createTextWrapperStyle = (horizontalGapLarge: number, horizontalGapMedium: number, horizontalGapSmall: number, verticalGapLarge: number, verticalGapMedium: number, verticalGapSmall: number) => StyleSheet.create({
  style: {
    overflow: 'hidden',
    width: '-webkit-fill-available',
    display: 'grid',
    [MEDIA_BREAKPOINTS.large]: {
      margin: `${baseUnit * horizontalGapLarge}px ${baseUnit * verticalGapLarge}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit * horizontalGapMedium}px ${baseUnit * verticalGapMedium}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit * horizontalGapSmall}px ${baseUnit * verticalGapSmall}px`,
    },
  },
}).style;

const createMainCategoryStyle = (
  showMainCategoryInLargeScreen: boolean,
  showMainCategoryInMediumScreen: boolean,
  showMainCategoryInSmallScreen: boolean,
  mainCategoryHorizontalGapLarge: number,
  mainCategoryHorizontalGapMedium: number,
  mainCategoryHorizontalGapSmall: number,
  mainCategoryVerticalGapLarge: number,
  mainCategoryVerticalGapMedium: number,
  mainCategoryVerticalGapSmall: number,
  mainCategoryBackgroundColor: string,
  mainCategoryTextColor: string,
) => StyleSheet.create({
  style: {
    backgroundColor: mainCategoryBackgroundColor,
    color: mainCategoryTextColor,
    position: 'absolute',
    bottom: 0,
    left: 0,
    [MEDIA_BREAKPOINTS.large]: {
      display: showMainCategoryInLargeScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapLarge * baseUnit}px ${mainCategoryVerticalGapLarge * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMainCategoryInMediumScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapMedium * baseUnit}px ${mainCategoryVerticalGapMedium * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMainCategoryInSmallScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapSmall * baseUnit}px ${mainCategoryVerticalGapSmall * baseUnit}px`,
    },
  },
}).style;

export const VerticalCard: React.FunctionComponent<VerticalCardProps> = props => {
  const {
    coverType,
    lazyLoad = true,
    showDecorationLine = true,
    title,
    articleUrl,
    image,
    imageWidths,
    authors,
    createdAt,
    mainCategory,
  } = props;
  const {
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    cardBackgroundColor,
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    showAuthorInLargeScreen,
    showAuthorInMediumScreen,
    showAuthorInSmallScreen,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    authorColor,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    metadataSemanticTag,
    showDateInSmallScreen,
    showDateInMediumScreen,
    showDateInLargeScreen,
    dateColor,
    pipeColor,
    fontSizesStyles,
    showMainCategoryInLargeScreen,
    showMainCategoryInMediumScreen,
    showMainCategoryInSmallScreen,
    mainCategoryHorizontalGapLarge,
    mainCategoryHorizontalGapMedium,
    mainCategoryHorizontalGapSmall,
    mainCategoryVerticalGapLarge,
    mainCategoryVerticalGapMedium,
    mainCategoryVerticalGapSmall,
    mainCategoryBackgroundColor,
    mainCategoryFontSizeLarge,
    mainCategoryFontSizeMedium,
    mainCategoryFontSizeSmall,
    mainCategorySemanticTag,
    mainCategoryTextColor,
    numberOfLinesForTitle,
    numberOfLinesForAuthors,
  } = useTheme<VerticalCardThemeProps>(componentConfigThemeKey);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
  };

  const fontSizeMedium = getLineHeightBySize(fontSizesStyles, titleFontSizeMedium, 'medium');
  const fontSizeLarge = getLineHeightBySize(fontSizesStyles, titleFontSizeLarge, 'large');

  const titleStyle = createStyle(cardBackgroundColor, cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius, fontSizeMedium, fontSizeLarge, numberOfLinesForTitle);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderBoxShadow });
  const authorsStyle = createAuthorsStyle(showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, authorColor, numberOfLinesForAuthors);
  const showMetadataInLargeScreen = showMetadataInLargeScreenSize(showAuthorInLargeScreen, showDateInLargeScreen);
  const showMetadataInMediumScreen = showMetadataInMediumScreenSize(showAuthorInMediumScreen, showDateInMediumScreen);
  const showMetadataInSmallScreen = showMetadataInSmallScreenSize(showAuthorInSmallScreen, showDateInSmallScreen);
  const showMainCategory = showMainCategoryInSmallScreen || showMainCategoryInMediumScreen || showMainCategoryInLargeScreen;
  const metadataStyle = createMetadataStyle(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, showMetadataInLargeScreen, showMetadataInMediumScreen, showMetadataInSmallScreen);
  const dateStyle = createDateStyle(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, dateColor);
  const showAuthor = showAuthorInAnyScreenSize(authors, showAuthorInLargeScreen, showAuthorInMediumScreen, showAuthorInSmallScreen);
  const showDate = showDateInAnyScreenSize(showDateInLargeScreen, showDateInMediumScreen, showDateInSmallScreen);
  const pipeStyle = createPipeStyle(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, pipeColor);
  // @ts-ignore TS2345
  const { style } = StyleSheet.create(ellipsify(numberOfLinesForTitle));
  const textWrapperStyle = createTextWrapperStyle(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, verticalGapLarge, verticalGapMedium, verticalGapSmall);

  const MainCategory: React.FunctionComponent = () => {
    const mainCategoryStyle = createMainCategoryStyle(
      showMainCategoryInLargeScreen,
      showMainCategoryInMediumScreen,
      showMainCategoryInSmallScreen,
      mainCategoryHorizontalGapLarge,
      mainCategoryHorizontalGapMedium,
      mainCategoryHorizontalGapSmall,
      mainCategoryVerticalGapLarge,
      mainCategoryVerticalGapMedium,
      mainCategoryVerticalGapSmall,
      mainCategoryBackgroundColor,
      mainCategoryTextColor,
    );
    return (
      <DynamicSemanticTag
        semanticTag={mainCategorySemanticTag}
        fontSizeLarge={mainCategoryFontSizeLarge}
        fontSizeMedium={mainCategoryFontSizeMedium}
        fontSizeSmall={mainCategoryFontSizeSmall}
        styles={semanticCSS(mainCategoryStyle)}
      >
        {mainCategory}
      </DynamicSemanticTag>
    );
  };

  return (
    <WrapperForCard
      boxShadowStyle={boxShadowStyle}
      borderRadius={borderRadius}
      paddingLarge={wrapperGapLarge}
      paddingMedium={wrapperGapMedium}
      paddingSmall={wrapperGapSmall}
    >
      <Link
        className={css(
          titleStyle.wrapper,
          boxShadowStyle.style,
        )}
        href={articleUrl}
      >
        <ConditionalRelativeWrap shouldWarp={coverType === 'video' || showMainCategory}>
          <Image {...image} lazyLoad={lazyLoad} imageWidths={imageWidths} imageAspectRatios={createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge)} />
          {coverType === 'video' ? <PlayIcon /> : null}
          {showMainCategory ? <MainCategory /> : null}
        </ConditionalRelativeWrap>
        {showDecorationLine ? <DecorationLine /> : null}
        <div className={css(textWrapperStyle)}>
          <header>
            <DynamicSemanticTag
              semanticTag={titleSemanticTag}
              fontSizeLarge={titleFontSizeLarge}
              fontSizeMedium={titleFontSizeMedium}
              fontSizeSmall={titleFontSizeSmall}
              styles={semanticCSS(titleStyle.text, style)}
            >
              {title}
            </DynamicSemanticTag>
          </header>
          {showMetadataInAnyScreenSize(showAuthor, showDate) ? (
            <footer>
              <DynamicSemanticTag
                semanticTag={metadataSemanticTag}
                fontSizeLarge={metadataFontSizeLarge}
                fontSizeMedium={metadataFontSizeMedium}
                fontSizeSmall={metadataFontSizeSmall}
                styles={semanticCSS(metadataStyle)}
              >
                {showAuthor ? (<div className={css(authorsStyle)}>{listAuthors(authors)}</div>) : null}
                {showPipeInAnyScreenSize(showAuthor, showDate) ? (<div className={css(pipeStyle)}>|</div>) : null}
                {showDate ? (<div className={css(dateStyle)}>{createdAt}</div>) : null}
              </DynamicSemanticTag>
            </footer>
          ) : null}
        </div>
      </Link>
    </WrapperForCard>
  );
};
